import uomService from 'Services/api/uom.service'
import { TOption } from 'components/OWForm/OWSelect'
import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import { isUndefined } from 'lodash-es'
import { UOMContract } from 'models'
import { useUOMsContext } from 'pages/UOMsPage/context/UOMsContext'
import { useEffect, useMemo } from 'react'
import { Dictionary } from 'utilities/typeUtility/typeUtility'

export const NO_REF_UOM_ID = -1

const useGetUOMAndDependencies = (uomID?: number) => {
    const [
        uom,
        getUOMByID,
        getUOMByIDStatus,
    ] = useFetchApi(uomService.getUOMByID, { showErrorToast: true })

    const { uoms, uomClasses } = useUOMsContext()

    const uomClasseOptions = useMemo(
        () =>
            uomClasses.map((c) => {
                return { value: c.uomClassID, label: c.name } as TOption
            }),
        [uomClasses],
    )

    const duplicatedUOMNames = useMemo(
        () =>
            uoms.filter((u) => u.uomid !== uomID).flatMap((i) => i.name ?? ''),
        [uoms, uomID],
    )

    const duplcatedUOMSymbols = useMemo(
        () =>
            // Make sure that all uom classsese existing
            uomClasseOptions.reduce((i, { value: uomClassID }) => {
                var uomsOfClass =
                    uoms
                        .filter(
                            (u) =>
                                u.uomid !== uomID &&
                                u.uomClassID === uomClassID,
                        )
                        .flatMap((i) => i.symbol!) ?? []
                i[uomClassID] = uomsOfClass
                return i
            }, {} as Record<number, string[]>) as Dictionary<string[]>,
        [uoms, uomID, uomClasseOptions],
    )

    const defaultUOMClassID = useMemo(
        () =>
            uomClasses.find((uc) => uc.isDefault)?.uomClassID ??
            uomClasses?.[0]?.uomClassID,
        [uomClasses],
    )

    const defaultUOM = useMemo(() => {
        return {
            uomClassID: defaultUOMClassID,
            enableConversion: false,
            initialUnitForConversion: 1,
            conversion: 1,
            isCustomData: true,
            isConversionModifiable: true,
            referenceUOMID: NO_REF_UOM_ID,
            isDefault: false,
        } as UOMContract
    }, [defaultUOMClassID])

    useEffect(() => {
        if (!isUndefined(uomID)) getUOMByID(uomID)
    }, [uomID, getUOMByID])

    return {
        uom: uom
            ? ({
                  ...uom,
                  referenceUOMID: uom.referenceUOMID ?? NO_REF_UOM_ID,
                  conversion:
                      (uom.conversion ?? 1) *
                      (uom.initialUnitForConversion ?? 1),
              } as UOMContract)
            : defaultUOM,
        loading: isLoading(getUOMByIDStatus),
        uoms: uoms.filter((u) => u.enableConversion),
        uomClasseOptions,
        duplicatedUOMNames,
        duplcatedUOMSymbols,
    }
}

export default useGetUOMAndDependencies
